import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type Dispatch, type MouseEvent, type ReactNode, type SetStateAction, useCallback } from "react";

import { INK_BLACK } from "../../../theme/tokens";
import { handleColumnSortClick, type SortClickBehavior, type SortingState } from "../../../utils/multiColumnSorting";

interface SortableDataGridHeaderProps<T> {
	/**
	 * Sort key(s) the header drives. Pass a single key for a one-column sort, or an array
	 * (matching a `MultiSortOption.sortColumns`) so a header click produces the same multi-key
	 * sort entry the popup would — toggling instead of stacking a single-key entry on top.
	 */
	field: keyof T | Array<keyof T>;
	/** Display label for the header. */
	headerName: string;
	/** Current multi-sort state (same reference used by SystemSortingButtonWithPopup). */
	sortState: SortingState<T>[];
	/** State setter (same reference used by SystemSortingButtonWithPopup). */
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	/**
	 * Optional row-level comparator carried into the sort entry created by a header click.
	 * Use when the column value isn't directly comparable (e.g. arrays/objects) and per-column
	 * `compareValues` won't yield a sensible order.
	 */
	customSortFunction?: (a: T, b: T) => 1 | 0 | -1;
	/**
	 * Optional rich tooltip shown on hover. Wraps the title in MUI Tooltip without
	 * blocking the surrounding click handler — so sort-on-click still fires.
	 */
	tooltipContent?: ReactNode;
	/**
	 * Header-click cycle for `clickBehavior: "stack"` only. Default (true) cycles asc → desc → removed
	 * (the third click drops the column from the sort); false keeps the sort on the column forever.
	 * Ignored under `"replace"`, which always keeps the sort — dropping the only key would leave the
	 * grid in fetch order, which reads as a bug.
	 */
	allowRemovalWhenDescendingToggle?: boolean;
	/**
	 * How a click resolves against existing sort keys. Defaults to "stack" — every click on an
	 * unsorted column inserts a new PRIMARY key and demotes the rest, which is what every existing
	 * caller was written against. Pass "replace" so a plain click makes this column the whole sort
	 * and shift+click appends it as a tie-breaker. See {@link SortClickBehavior}.
	 */
	clickBehavior?: SortClickBehavior;
	/** "replace" only — entries this approves are never dropped by a replace. */
	retainEntry?: (entry: SortingState<T>) => boolean;
}

/**
 * A DataGrid column header that integrates with external multi-column sort state.
 *
 * Shows a sort arrow + priority number when the column participates in the current multi-sort.
 * A click stacks this column on top of the existing keys by default; under `clickBehavior: "replace"`
 * it becomes the whole sort instead, with shift+click appending it as a lower-priority tie-breaker.
 *
 * Usage — pass via `renderHeader` on a `GridColDef`:
 * ```tsx
 * renderHeader: () => (
 *   <SortableDataGridHeader field="name" headerName="Name" sortState={sortState} setSortState={setSortState} />
 * )
 * ```
 */
export const SortableDataGridHeader = <T,>({
	field,
	headerName,
	sortState,
	setSortState,
	customSortFunction,
	tooltipContent,
	allowRemovalWhenDescendingToggle = true,
	// DEFAULT: stack — the behavior every existing caller was written against. A grid that wants a
	// header click to mean "sort by this column instead" passes "replace" (SystemDataGrid's `headerClickMode`).
	clickBehavior = "stack",
	retainEntry,
}: SortableDataGridHeaderProps<T>) => {
	const fieldCols = Array.isArray(field) ? field : [field];
	const primaryField = fieldCols[0];
	// Match by primary key so the arrow appears on the header that owns the sort, even when
	// the entry uses additional tie-break columns the caller wired in via `field`.
	const sortIndex = sortState.findIndex((s) => s.sortColumns[0] === primaryField);
	const sortEntry = sortIndex >= 0 ? sortState[sortIndex] : null;
	const showPriority = sortState.length > 1 && sortEntry;

	const handleClick = useCallback(
		(event: MouseEvent<HTMLDivElement>) => {
			// shift+click is the only way to build a multi-key sort by mouse once stacking is off.
			const behavior: SortClickBehavior = clickBehavior === "replace" && event.shiftKey ? "append" : clickBehavior;
			handleColumnSortClick({
				sortState,
				setSortState,
				columns: fieldCols,
				label: headerName,
				customSortFunction,
				allowRemovalWhenDescendingToggle,
				clickBehavior: behavior,
				retainEntry,
			});
		},
		[sortState, setSortState, fieldCols, headerName, customSortFunction, allowRemovalWhenDescendingToggle, clickBehavior, retainEntry],
	);

	return (
		<Box
			onClick={handleClick}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				cursor: "pointer",
				userSelect: "none",
				width: "100%",
				"&:hover": {
					background: alpha(INK_BLACK, 0.04),
					borderRadius: 2,
				},
				px: 0.5,
			}}
		>
			{tooltipContent ? (
				<Tooltip title={tooltipContent} arrow placement="top">
					<Typography variant="body2" sx={{ fontWeight: 700, fontSize: "inherit", lineHeight: "inherit", cursor: "help" }}>
						{headerName}
					</Typography>
				</Tooltip>
			) : (
				<Typography variant="body2" sx={{ fontWeight: 700, fontSize: "inherit", lineHeight: "inherit" }}>
					{headerName}
				</Typography>
			)}
			{sortEntry && (
				<Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
					{sortEntry.sortOrder === "asc" ? (
						<ArrowUpwardIcon sx={{ fontSize: 16, color: "primary.main" }} />
					) : (
						<ArrowDownwardIcon sx={{ fontSize: 16, color: "primary.main" }} />
					)}
					{showPriority && (
						<Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "primary.main", lineHeight: 1 }}>
							{sortIndex + 1}
						</Typography>
					)}
				</Box>
			)}
		</Box>
	);
};
